import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { Router } from '@angular/router';
import {
  AbstractControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Card } from 'primeng/card';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { Button } from 'primeng/button';
import { MessageService, SharedModule } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  map,
  startWith,
  tap,
} from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ControlErrorDirective } from '../../../../../shared/directive/control-error.directive';
import { QuillListMarkerStyleDirective } from '../../../../../shared/directive/quill-list-marker-style.directive';
import { RemoveQuillTooltipDirective } from '../../../../../shared/directive/remove-quill-tooltip.directive';
import { RequiredLabelDirective } from '../../../../../shared/directive/required-label.directive';
import { FormsUtils } from '../../../../../shared/utils/forms.utils';
import { AnnouncementDetails } from '../../../../../shared/models/announcement-details';
import { AnnouncementEditFormBuilder } from './form-group/announcement-edit-form.builder';
import { AnnouncementEditFormModel } from './form-group/announcement-edit-form.model';
import {
  ANNOUNCEMENT_STATUS_LABELS,
  AnnouncementStatus,
} from '../../../../../shared/enums/announcement-status.enum';
import { AnnouncementChannel } from '../../../../../shared/enums/announcement-channel.enum';
import { AnnouncementRecipientType } from '../../../../../shared/enums/announcement-recipient-type.enum';
import { AnnouncementConfirmationRequirement } from '../../../../../shared/enums/announcement-confirmation-requirement.enum';
import { APP_PATHS } from '../../../../../core/config/app-paths';
import { ToastSeverity } from '../../../../../shared/enums/toast-severity.enum';
import { Editor } from 'primeng/editor';
import { AnnouncementRecipientDialog } from '../announcement-recipient-dialog/announcement-recipient-dialog';
import { AnnouncementRecipient } from '../../../../../shared/models/announcement-recipient';
import {
  ANNOUNCEMENT_CHANNEL_OPTIONS,
  ANNOUNCEMENT_CONFIRMATION_OPTIONS,
  ANNOUNCEMENT_RECIPIENT_TYPE_OPTIONS,
} from '../../../../../shared/constants/announcement-form-options';
import { DateValidators } from '../../../../../shared/validators/date-validators';
import { SharedValidators } from '../../../../../shared/validators/shared-validators';
import { AnnouncementRecipientsComponent } from '../../components/announcement-recipients/announcement-recipients.component';
import { AnnouncementsApi } from '../../../../../shared/services/api/announcements.api';
import { SpinnerService } from '../../../../../core/services/spinner.service';
import { HttpResponseService } from '../../../../../core/services/http-error-response.service';
import { DokumentApi } from '../../../../../shared/services/api/dokument.api';

@Component({
  selector: 'app-admin-panel-announcement-edit',
  imports: [
    Button,
    Card,
    ControlErrorDirective,
    DatePicker,
    Editor,
    InputText,
    RemoveQuillTooltipDirective,
    QuillListMarkerStyleDirective,
    ReactiveFormsModule,
    RequiredLabelDirective,
    Select,
    NgClass,
    SharedModule,
    AnnouncementRecipientsComponent,
  ],
  templateUrl: './admin-panel-announcement-edit.html',
  styleUrl: './admin-panel-announcement-edit.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminPanelAnnouncementEdit implements OnInit {
  protected announcement = input<AnnouncementDetails | undefined>();
  protected formGroup = signal<FormGroup<AnnouncementEditFormModel> | null>(
    null,
  );
  protected readonly channelOptions = ANNOUNCEMENT_CHANNEL_OPTIONS;
  protected readonly confirmationOptions = ANNOUNCEMENT_CONFIRMATION_OPTIONS;
  protected readonly recipientTypeOptions = ANNOUNCEMENT_RECIPIENT_TYPE_OPTIONS;
  protected readonly today = new Date();
  protected readonly recipients = signal<AnnouncementRecipient[]>([]);
  protected statusLabel = computed(() => {
    return (
      ANNOUNCEMENT_STATUS_LABELS[this.formGroup()?.controls.status.value!] ??
      '-'
    );
  });
  protected statusPillClass = computed(() => {
    switch (this.formGroup()?.controls.status.value) {
      case AnnouncementStatus.ROBOCZY:
        return 'status-pill status-draft';
      case AnnouncementStatus.OPUBLIKOWANY:
        return 'status-pill status-published';
      case AnnouncementStatus.ARCHIWALNY:
        return 'status-pill status-archived';
      case AnnouncementStatus.BLAD:
        return 'status-pill status-error';
      default:
        return 'status-pill';
    }
  });
  readonly #router = inject(Router);
  readonly #messageService = inject(MessageService);
  readonly #dialogService = inject(DialogService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #announcementsApi = inject(AnnouncementsApi);
  readonly #dokumentApi = inject(DokumentApi);
  protected readonly requestTypeOptions = toSignal(
    this.#dokumentApi.getDokumentTypesCodes().pipe(
      map((data) =>
        data.map((value) => ({
          label: value,
          value: value,
        })),
      ),
      catchError((err) => {
        this.#httpResponseService.showErrorMessage(
          err,
          'Wystąpił błąd podczas pobierania niezbędnych danych do wypełnienia formularza',
        );
        return EMPTY;
      }),
    ),
    { initialValue: [] },
  );
  readonly #spinnerService = inject(SpinnerService);
  readonly #httpResponseService = inject(HttpResponseService);

  ngOnInit(): void {
    this.formGroup.set(
      AnnouncementEditFormBuilder.createFormGroup(this.announcement()!),
    );
    this.recipients.set(this.announcement()?.recipients ?? []);
    this.#applyDateValidators();
    this.#applyConditionalValidators();
  }

  protected shouldShowSystemFields(): boolean {
    const channel = this.formGroup()?.controls.channel.value;
    return (
      channel === AnnouncementChannel.SYSTEM ||
      channel === AnnouncementChannel.SYSTEM_I_EMAIL
    );
  }

  protected shouldShowEmailFields(): boolean {
    const channel = this.formGroup()?.controls.channel.value;
    return (
      channel === AnnouncementChannel.EMAIL ||
      channel === AnnouncementChannel.SYSTEM_I_EMAIL
    );
  }

  protected shouldShowRecipients(): boolean {
    return (
      this.formGroup()?.controls.recipientType.value ===
      AnnouncementRecipientType.INDYWIDUALNY
    );
  }

  protected shouldShowRequestType(): boolean {
    return (
      this.formGroup()?.controls.recipientType.value ===
      AnnouncementRecipientType.WNIOSEK
    );
  }

  protected onSelectRecipients(): void {
    const ref = this.#dialogService.open(AnnouncementRecipientDialog, {
      header: 'Wybierz adresata',
      closable: true,
      modal: true,
      closeOnEscape: true,
      dismissableMask: true,
      width: '1100px',
      data: {
        selectedRecipients: this.formGroup()?.controls.recipients.value ?? [],
      },
      breakpoints: {
        '960px': '95vw',
        '640px': '95vw',
      },
    });

    if (!ref) {
      return;
    }

    ref.onClose
      .pipe(filter(Boolean), takeUntilDestroyed(this.#destroyRef))
      .subscribe((recipients: AnnouncementRecipient[]) => {
        this.recipients.set(recipients);
        this.formGroup()?.controls.recipients.setValue(recipients);
        this.formGroup()?.controls.recipients.updateValueAndValidity({
          emitEvent: false,
        });
      });
  }

  protected onRemoveRecipient(recipientToRemove: AnnouncementRecipient): void {
    const recipients = this.recipients().filter(
      ({ email }) => email !== recipientToRemove.email,
    );
    this.recipients.set(recipients);
    this.formGroup()?.controls.recipients.setValue(recipients);
    this.formGroup()?.controls.recipients.updateValueAndValidity({
      emitEvent: false,
    });
  }

  protected onSave(): void {
    if (!this.formGroup() || !FormsUtils.isReadyForSave(this.formGroup()!)) {
      this.#messageService.add({
        severity: ToastSeverity.ERROR,
        summary: 'Wystąpił błąd podczas walidacji formularza',
      });
      return;
    }

    const payload = this.formGroup()!.getRawValue() as AnnouncementDetails;
    if (!payload.id) {
      return;
    }

    this.#spinnerService.show();
    this.#announcementsApi
      .updateAnnouncement$(payload)
      .pipe(
        tap(() => {
          this.#httpResponseService.showSuccessMessage(
            'Poprawnie zapisano komunikat.',
          );
          this.#navigateToDetails(payload.id!);
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas próby zapisu komunikatu.',
          );
          return EMPTY;
        }),
        finalize(() => this.#spinnerService.hide()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected onCancel(): void {
    const announcementId = this.formGroup()?.controls?.id?.value;
    if (!announcementId) {
      return;
    }

    this.#navigateToDetails(announcementId)
  }

  #navigateToDetails(applicationId: number): void {
    this.#router.navigate([
      `/${APP_PATHS.admin.root}/${APP_PATHS.admin.announcements}/${applicationId}/${APP_PATHS.admin.details}`,
    ]);
  }

  #applyConditionalValidators(): void {
    if (!this.formGroup()) {
      return;
    }

    this.formGroup()!.controls.requiresConfirmation.addValidators(
      SharedValidators.when(Validators.required, () =>
        this.shouldShowSystemFields(),
      ),
    );

    this.formGroup()!.controls.startDate.addValidators(
      SharedValidators.when(Validators.required, () =>
        this.shouldShowSystemFields(),
      ),
    );

    this.formGroup()!.controls.endDate.addValidators(
      SharedValidators.when(Validators.required, () =>
        this.shouldShowSystemFields(),
      ),
    );

    this.formGroup()!.controls.plannedEmailSendAt.addValidators(
      SharedValidators.when(Validators.required, () =>
        this.shouldShowEmailFields(),
      ),
    );

    this.formGroup()!.controls.recipients.addValidators(
      SharedValidators.when(Validators.required, () =>
        this.shouldShowRecipients(),
      ),
    );

    this.formGroup()!.controls.requestCode.addValidators(
      SharedValidators.when(Validators.required, () =>
        this.shouldShowRequestType(),
      ),
    );

    this.formGroup()!
      .controls.channel.valueChanges.pipe(
        startWith(this.formGroup()!.controls.channel.value),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        this.#applyConditionalRules();
        this.formGroup()!.updateValueAndValidity({ emitEvent: false });
      });

    this.formGroup()!
      .controls.recipientType.valueChanges.pipe(
        startWith(this.formGroup()!.controls.recipientType.value),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        this.#applyConditionalRules();
        this.formGroup()!.updateValueAndValidity({ emitEvent: false });
      });

    this.#applyConditionalRules();
    this.formGroup()!.updateValueAndValidity({ emitEvent: false });
  }

  #applyConditionalRules(): void {
    if (!this.formGroup()) {
      return;
    }

    const channel = this.formGroup()!.controls.channel.value;
    const recipientType = this.formGroup()!.controls.recipientType.value;

    const isSystemChannel =
      channel === AnnouncementChannel.SYSTEM ||
      channel === AnnouncementChannel.SYSTEM_I_EMAIL;

    const isEmailChannel =
      channel === AnnouncementChannel.EMAIL ||
      channel === AnnouncementChannel.SYSTEM_I_EMAIL;

    const isIndividual =
      recipientType === AnnouncementRecipientType.INDYWIDUALNY;
    const isRequest = recipientType === AnnouncementRecipientType.WNIOSEK;

    if (!isSystemChannel) {
      this.formGroup()!.controls.requiresConfirmation.setValue(
        AnnouncementConfirmationRequirement.NIE_DOTYCZY,
        { emitEvent: false },
      );
      this.#clearControl(this.formGroup()!.controls.startDate);
      this.#clearControl(this.formGroup()!.controls.endDate);
    } else if (
      this.formGroup()!.controls.requiresConfirmation.value ===
      AnnouncementConfirmationRequirement.NIE_DOTYCZY
    ) {
      this.formGroup()!.controls.requiresConfirmation.setValue(null, {
        emitEvent: false,
      });
    }

    if (!isEmailChannel) {
      this.#clearControl(this.formGroup()!.controls.plannedEmailSendAt);
    }

    if (!isIndividual) {
      this.recipients.set([]);
      this.#clearControl(this.formGroup()!.controls.recipients);
    }

    if (!isRequest) {
      this.#clearControl(this.formGroup()!.controls.requestCode);
    }
  }

  #applyDateValidators(): void {
    if (!this.formGroup()) {
      return;
    }

    this.formGroup()!.setValidators([
      DateValidators.startNotAfterEnd('startDate', 'endDate'),
    ]);

    this.formGroup()!.controls.plannedEmailSendAt.addValidators(
      DateValidators.notInPast(),
    );
  }

  #clearControl(control: AbstractControl): void {
    control.setValue(null, { emitEvent: false });
    control.markAsPristine();
    control.markAsUntouched();
  }
}
